import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  BellRing,
  CalendarX2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Globe,
  Mail,
  MapPin,
  MessageSquare,
  User,
  Zap,
} from 'lucide-react';
import { textIn, type AnswerError, type Answers } from '@rx-manager/forms';
import type { DraftAnswers } from '@rx-manager/forms/react';
import {
  zonedDateOf,
  zonedDayWindow,
  zonedToday,
  type AvailabilitySlot,
  type PublicBookingForm,
  type PublicBookingPage,
  type PublicBookingResult,
  type PublicClinicInfo,
  type PublicCondition,
  type PublicLocation,
  type PublicPatientDetails,
  type PublicService,
  type PublicTextReminderWording,
} from '@rx-manager/shared';
import { bookingApi, BookingApiError } from '../lib/api';
import { intlLocale, useI18n, type MessageKey } from '../lib/i18n';
import {
  hasErrors,
  safeFormSchema,
  validateIntake,
  validatePatient,
  type FieldErrors,
} from '../lib/validate';
import {
  Alert,
  Button,
  CHECKBOX,
  Card,
  ChoiceCard,
  Chip,
  Field,
  FOCUS_RING,
  HEADING,
  IconTile,
  Input,
  StepActions,
  SuccessMark,
  buttonClasses,
  cn,
} from '../components/ui';
import { AppointmentHead, Detail } from '../components/AppointmentCard';
import { IntakeForm } from '../components/IntakeForm';
import { SlotGrid } from '../components/SlotGrid';
import { WeekStrip, addDays, daysBetween, pageStartFor } from '../components/WeekStrip';
import { ServicePicker, categoryIcon, conditionText } from '../components/ServicePicker';
import { FormsDue } from './forms/FormsDue';
import { TimeoutDialog, usePrivacyTimeout } from './forms/PrivacyTimeout';
import {
  NO_FORMS,
  confirmForms,
  firstUnsettled,
  formLang,
  formsReducer,
  hasForms,
  type BookingFormsState,
} from './booking/booking-forms';
import { FormsSummary } from './booking/FormsSummary';
import { QuestionsStep } from './booking/QuestionsStep';
import { TextRemindersBox, hashOf, useHoldCountdown } from './booking/shared';
import { WalkInNote, WalkInStep } from './booking/WalkIn';
import { WaitlistJoin } from './waitlist/WaitlistJoin';
import { StepBar } from '../components/StepBar';
import { PeopleStep } from './booking/party/PeopleStep';
import { PartyFlow } from './booking/party/PartyFlow';
import { ClinicsList } from './clinic/Clinic';

/**
 * The patient's whole journey: pick a service, a branch, a time, say who you
 * are, then check it all before confirming.
 *
 * One page with five steps rather than five routes. A patient on a phone who
 * hits Back expects to lose a step, not the booking — and a held slot must not
 * be stranded by a navigation the app never sees. Steps are state; the URL stays
 * the pharmacy's.
 *
 * Track RBA — where the pharmacy texts appointment reminders (`page.textReminders`),
 * the details step offers "Text me appointment reminders at this number" under the
 * phone, unticked, with the words agreed to in the page's language. The review says
 * which was chosen, and the confirm sends `textReminders` and `language` — only when
 * the box was offered, so a page without it books exactly as before.
 *
 * Track SFM rev 2 — a service with forms asks them here, one question at a time, after the
 * time and before the patient's details (SFM-D26), as MedEssist does. The time is held only
 * once the questions are done, so the ten-minute hold still covers just the details and the
 * review; a time taken meanwhile sends the patient back to pick another, answers kept. The
 * answers go with Confirm and are written with the booking; a refusal brings the patient back
 * to the form it names. They live in memory only, and clear after 20 idle minutes (SFM-D35).
 *
 * Track BCW — a patient who comes in by a condition ("What do you need help with?") answers the
 * questions BEFORE the time (BCW-D5), as MedEssist's minor-ailment booking does: a stop answer
 * ends it before a time is chosen, and the time is held only when they leave the time step. The
 * booking says which condition it was for.
 */

type Step = 'service' | 'location' | 'people' | 'time' | 'questions' | 'details' | 'review';

/** Track CGS — "How many people?" only where a party can book (CGS-D9); one person is as before. */
const STEP_ORDER: Step[] = ['service', 'location', 'people', 'time', 'questions', 'details', 'review'];
/** BCW-D5 — by condition, the questions come before the time. */
const CONDITION_STEP_ORDER: Step[] = ['service', 'location', 'people', 'questions', 'time', 'details', 'review'];

const STEP_LABEL: Record<Step, MessageKey> = {
  service: 'step.service',
  location: 'step.location',
  people: 'step.people',
  time: 'step.time',
  questions: 'step.questions',
  details: 'step.details',
  review: 'step.review',
};

/** Answers kept in memory clear after this long with nothing done (SFM-D35, as a form link). */
const IDLE_MS = 20 * 60 * 1000;

const STEP_HEADING: Record<Exclude<Step, 'time' | 'questions'>, MessageKey> = {
  service: 'service.heading',
  location: 'location.heading',
  people: 'people.heading',
  details: 'details.heading',
  review: 'review.heading',
};

/**
 * Whether the confirmation can promise a message (Q8). Until the pharmacy's
 * confirmation is known to reach the patient, the page says only what is true:
 * keep this page. Flip when delivery is confirmed.
 */
export const CONFIRMATION_IS_SENT = false;


const emptyPatient = (): Partial<PublicPatientDetails> => ({
  firstName: '',
  lastName: '',
  dob: '',
  email: '',
  phone: '',
});

export function BookingFlow({
  page,
  initialServiceId,
  initialConditionCode,
  clinic,
  clinicHref,
}: {
  page: PublicBookingPage;
  /** Chosen already, by a deep link from the pharmacy's own site. */
  initialServiceId?: string;
  /** Track BCW — a condition chosen already, by a `/<slug>/c/<code>` link. */
  initialConditionCode?: string;
  /** Track CGS (CGS-D3) — on a clinic's own page: its dates bound the times. */
  clinic?: PublicClinicInfo;
  /** Track CGS (CGS-D2) — where a listed clinic's own page is. */
  clinicHref?: (code: string) => string;
}) {
  // The profile already resolved the tenant; reuse its slug rather than
  // re-deriving it, so the flow cannot disagree with the shell about which
  // pharmacy it is booking at.
  const slug = page.profile.slug;
  const [params] = useSearchParams();
  const invitationToken = params.get('invitation') ?? undefined;
  /**
   * A time already chosen in the picker embedded on the pharmacy's own site.
   *
   * Advisory, exactly like every other slot the patient can see: it is not a
   * hold, and the server re-checks it at hold time. A slot that went while they
   * were crossing between sites simply fails the same way any other stale slot
   * does.
   */
  const deepLinkedStart = params.get('start') ?? undefined;
  const { t, locale } = useI18n();

  const [step, setStep] = useState<Step>('service');
  /** Track BCW — the condition the patient came in by, if any (BCW-D5). */
  const [conditionId, setConditionId] = useState('');
  /** Track BCW — joining the waitlist instead of picking a time (BCW-D9). */
  const [joining, setJoining] = useState(false);
  /** Track CGS — how many people this booking is for; one is today's flow (CGS-D9). */
  const [partySize, setPartySize] = useState(1);
  const [serviceId, setServiceId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [date, setDate] = useState('');
  const [slotStart, setSlotStart] = useState<string | null>(null);
  const [hold, setHold] = useState<{ token: string; expiresAt: string } | null>(null);
  const [patient, setPatient] = useState(emptyPatient);
  const [intake, setIntake] = useState<Record<string, unknown>>({});
  const [consent, setConsent] = useState(false);
  /** Track RBA — the "text me reminders" box. Never ticked for the patient. */
  const [texts, setTexts] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [failure, setFailure] = useState<Failure | null>(null);
  const [booked, setBooked] = useState<PublicBookingResult | null>(null);
  const [busy, setBusy] = useState(false);
  /** A hold is being taken: set and cleared with it, so a second tap cannot start another. */
  const holding = useRef(false);
  /** Track SFM rev 2 — the service's forms, asked inside the booking. */
  const [forms, dispatch] = useReducer(formsReducer, NO_FORMS);
  /** The forms load in flight, resolving to the list, or null when it failed. */
  const formsLoading = useRef<Promise<PublicBookingForm[] | null> | null>(null);
  const [idleUntil, setIdleUntil] = useState<string | null>(null);
  const lastActivity = useRef(0);

  const headingRef = useRef<HTMLHeadingElement>(null);
  /** Idempotency key per attempt, so a retry cannot double-book. */
  const attemptKey = useRef(cryptoId());

  const service = page.services.find((entry) => entry.id === serviceId) ?? null;
  /** BCW-D6 — a walk-in service shows when to come instead of times. */
  const walkIn = service?.bookingMode === 'walk_in';
  /** BCW-D8 — "None of these work? Join the waitlist", where the page offers it. */
  const offersWaitlist = Boolean(service?.waitlist && page.waitlist?.available);
  /** Track CGS (CGS-D9) — the People step, where one booking can bring more than one. */
  const partyOf = (entry: PublicService | null): entry is PublicService & { party: { max: number; extraMin: number } } =>
    Boolean(entry?.party && entry.party.max > 1 && entry.bookingMode !== 'walk_in');
  const offersParty = partyOf(service);
  /** Track CGS (CGS-D4) — on a clinic's page, its dates bound the times. */
  const clinicDates = useMemo(() => {
    if (!clinic) return undefined;
    const today = zonedToday(clinic.timezone);
    const first = clinic.sessions.find((session) => session.date >= today)?.date ?? clinic.startsOn;
    return { from: first > today ? first : today, to: clinic.endsOn };
  }, [clinic]);
  /** Track CGS (CGS-D19) — a clinic's ISO weekdays still ahead, for a waitlist place there. */
  const clinicWeekdays = useMemo(() => {
    if (!clinic) return undefined;
    const today = zonedToday(clinic.timezone);
    const weekdays = clinic.sessions
      .filter((session) => session.date >= today)
      .map((session) => ((new Date(`${session.date}T12:00:00Z`).getUTCDay() + 6) % 7) + 1);
    return [...new Set(weekdays)].sort((a, b) => a - b);
  }, [clinic]);
  /** The first day the time step shows: today, or a clinic's first date ahead. */
  const firstDayAt = (timeZone: string): string => clinicDates?.from ?? zonedToday(timeZone);
  const conditions = page.conditions ?? [];
  const condition = conditions.find((entry) => entry.id === conditionId) ?? null;
  const byCondition = condition !== null;
  /** The services the chosen condition leads to that this page lists, in its order. */
  const conditionServices = useMemo(
    () =>
      condition
        ? condition.serviceIds
            .map((id) => page.services.find((entry) => entry.id === id))
            .filter((entry): entry is PublicService => Boolean(entry))
        : [],
    [condition, page.services],
  );
  const textOffer = page.textReminders ?? null;
  /** The box is about "this number", so it is only offered — and only counts — once there is one. */
  const textsChosen = texts && Boolean(patient.phone?.trim());
  const location = page.locations.find((entry) => entry.id === locationId) ?? null;
  const formSchema = useMemo(
    () => safeFormSchema(service?.formSchema),
    [service?.formSchema],
  );

  /** Sites that can actually deliver the chosen service. */
  const eligibleLocations = useMemo(
    () =>
      service?.locationId
        ? page.locations.filter((entry) => entry.id === service.locationId)
        : page.locations,
    [page.locations, service],
  );

  /**
   * Skip the steps a deep link has already answered.
   *
   * Runs once. Anything the patient does afterwards — including going back to
   * change the service — must not be undone by this effect re-firing, which is
   * what the ref guards.
   */
  const deepLinkApplied = useRef(false);
  useEffect(() => {
    if (deepLinkApplied.current || invitationToken) return;
    const chosen = page.services.find((entry) => entry.id === initialServiceId);
    if (!chosen) return;
    deepLinkApplied.current = true;

    setServiceId(chosen.id);
    const sites = chosen.locationId
      ? page.locations.filter((entry) => entry.id === chosen.locationId)
      : page.locations;
    // One site is not a choice, so a deep link with one site lands on the times.
    if (sites.length === 1) {
      const site = sites[0]!;
      setLocationId(site.id);
      setDate(
        deepLinkedStart
          ? zonedDateOf(new Date(deepLinkedStart), site.timezone)
          : zonedToday(site.timezone),
      );
      if (deepLinkedStart) setSlotStart(deepLinkedStart);
      setStep('time');
    } else {
      setStep('location');
    }
  }, [initialServiceId, deepLinkedStart, invitationToken, page]);

  /** Track BCW — a `/c/<code>` link: the condition chosen, its only service too. */
  const conditionLinkApplied = useRef(false);
  useEffect(() => {
    if (conditionLinkApplied.current || invitationToken || !initialConditionCode) return;
    const chosen = (page.conditions ?? []).find((entry) => entry.code === initialConditionCode);
    if (!chosen) return;
    conditionLinkApplied.current = true;
    // Runs once on arrival; chooseCondition reads only the page.
    chooseCondition(chosen);
  }, [initialConditionCode, invitationToken, page]);

  // Pre-fill from a "pick your own time" link.
  useEffect(() => {
    if (!invitationToken) return;
    let cancelled = false;
    void bookingApi
      .invitation(slug, invitationToken)
      .then((prefill) => {
        if (cancelled) return;
        setServiceId(prefill.serviceId);
        // The branch the invitation names, or the only one that offers the service; with
        // either, the patient lands on the times for today in that branch's zone.
        const invited = page.services.find((entry) => entry.id === prefill.serviceId);
        const sites = invited?.locationId
          ? page.locations.filter((entry) => entry.id === invited.locationId)
          : page.locations;
        const site =
          page.locations.find((entry) => entry.id === prefill.locationId) ??
          (sites.length === 1 ? sites[0] : undefined);
        if (site) {
          setLocationId(site.id);
          setDate(zonedToday(site.timezone));
        }
        setPatient((current) => ({
          ...current,
          firstName: prefill.firstName,
          lastName: prefill.lastName,
          email: prefill.email ?? '',
          phone: prefill.phone ?? '',
        }));
        setStep(site ? 'time' : 'location');
      })
      // A dead invitation is not a dead booking — let them start normally.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [invitationToken, slug, page]);

  /**
   * Move focus to the new step's heading.
   *
   * Without this a screen reader stays where the old button was and announces
   * nothing, so the patient has no idea the page changed.
   */
  useEffect(() => {
    headingRef.current?.focus();
  }, [step, booked]);

  /**
   * Track SFM rev 2 — the chosen service's forms, fetched as soon as it is chosen, so they
   * are there by the time the patient has picked a time. A failure is said, never taken for
   * "no forms": a required form skipped here would only be refused at Confirm.
   */
  const loadForms = useCallback(
    (id: string): Promise<PublicBookingForm[] | null> => {
      dispatch({ type: 'load_start', serviceId: id });
      const run = bookingApi
        .serviceForms(slug, id)
        .then((list) => {
          dispatch({ type: 'loaded', serviceId: id, forms: list });
          return list;
        })
        .catch(() => {
          dispatch({ type: 'load_failed', serviceId: id });
          return null;
        });
      formsLoading.current = run;
      return run;
    },
    [slug],
  );
  useEffect(() => {
    if (serviceId) void loadForms(serviceId);
  }, [serviceId, loadForms]);

  /**
   * Only the steps that apply. A branch step with one branch was never a
   * choice, so it is neither shown in the step bar nor stepped back into
   * (A5 FE-P-03); the questions only for a service that asks some.
   */
  const steps = (byCondition ? CONDITION_STEP_ORDER : STEP_ORDER).filter(
    (entry) =>
      (entry !== 'location' || eligibleLocations.length > 1) &&
      (entry !== 'people' || offersParty) &&
      (entry !== 'questions' || (hasForms(forms) && !walkIn)) &&
      // A walk-in ends at its hours: nothing to fill in or confirm.
      (!walkIn || (entry !== 'details' && entry !== 'review')),
  );
  const stepIndex = Math.max(steps.indexOf(step), 0);

  const goBack = (): void => {
    setFailure(null);
    // Stepping back from the form gives the slot up rather than sitting on it
    // for ten minutes while the patient reconsiders. Review → details keeps it.
    if (step === 'details') void releaseHold();
    // BCW-D5 — by condition the time comes after the questions: details → the time,
    // and the time → the last form.
    if (byCondition && step === 'details') {
      setStep('time');
      return;
    }
    if (byCondition && step === 'time' && hasForms(forms)) {
      dispatch({ type: 'open', index: forms.entries.length - 1, returnTo: null });
      setStep('questions');
      return;
    }
    if (byCondition && step === 'location' && conditionServices.length <= 1) {
      leaveCondition();
      return;
    }
    if (step === 'details' && hasForms(forms)) {
      // Back to the last form, on its review (or its intro, if it was skipped).
      dispatch({ type: 'open', index: forms.entries.length - 1, returnTo: null });
      setStep('questions');
      return;
    }
    setStep(steps[Math.max(stepIndex - 1, 0)]!);
  };

  const releaseHold = useCallback(async (): Promise<void> => {
    if (!hold) return;
    const token = hold.token;
    setHold(null);
    await bookingApi.releaseHold(slug, token).catch(() => undefined);
  }, [hold, slug]);

  const chooseService = (next: PublicService, viaCondition = byCondition): void => {
    if (next.id !== serviceId) dispatch({ type: 'reset' });
    setServiceId(next.id);
    setSlotStart(null);
    // BCW-D6 — a waitlist service is booked only by joining; its questions come with the offer.
    if (next.bookingMode === 'waitlist') {
      setJoining(true);
      return;
    }
    const sites = next.locationId
      ? page.locations.filter((entry) => entry.id === next.locationId)
      : page.locations;
    // One site is not a choice; do not make them tap through it.
    if (sites.length === 1) {
      setLocationId(sites[0]!.id);
      setDate(firstDayAt(sites[0]!.timezone));
      // Track CGS — how many people, before anything else is asked (CGS-D9).
      if (partyOf(next)) {
        setPartySize(1);
        setStep('people');
      } else if (viaCondition && next.bookingMode !== 'walk_in') void questionsFirst(next);
      else setStep('time');
    } else {
      setStep('location');
    }
  };

  /** A service from the full list: whatever condition was chosen no longer applies. */
  const chooseListedService = (next: PublicService): void => {
    setConditionId('');
    chooseService(next, false);
  };

  /**
   * Track BCW (BCW-D5) — a condition: its only service is chosen with it; with two or three, the
   * service step lists them.
   */
  const chooseCondition = (next: PublicCondition): void => {
    setConditionId(next.id);
    setFailure(null);
    const listed = next.serviceIds
      .map((id) => page.services.find((entry) => entry.id === id))
      .filter((entry): entry is PublicService => Boolean(entry));
    if (listed.length === 1) {
      chooseService(listed[0]!, true);
    } else {
      dispatch({ type: 'reset' });
      setServiceId('');
      setStep('service');
    }
  };

  /** Back to the full list: no condition, no service. */
  const leaveCondition = (): void => {
    dispatch({ type: 'reset' });
    setConditionId('');
    setServiceId('');
    setSlotStart(null);
    setStep('service');
  };

  /**
   * BCW-D5 — by condition, the service's questions before the time: the first one still to
   * answer, or straight to the times when it asks none.
   */
  const questionsFirst = async (chosen: PublicService): Promise<void> => {
    setFailure(null);
    setBusy(true);
    const pending =
      forms.serviceId === chosen.id && formsLoading.current ? formsLoading.current : loadForms(chosen.id);
    const list = await pending;
    setBusy(false);
    if (list === null) {
      setFailure({ key: 'questions.loadFailed' });
      return;
    }
    const state = formsReducer(formsReducer(NO_FORMS, { type: 'load_start', serviceId: chosen.id }), {
      type: 'loaded',
      serviceId: chosen.id,
      forms: list,
    });
    const next = hasForms(state) ? firstUnsettled(state) : -1;
    if (next < 0) {
      setStep('time');
      return;
    }
    dispatch({ type: 'open', index: next, returnTo: null });
    setStep('questions');
  };

  const chooseLocation = (next: PublicLocation): void => {
    setLocationId(next.id);
    setDate(firstDayAt(next.timezone));
    setSlotStart(null);
    if (offersParty) {
      setPartySize(1);
      setStep('people');
    } else if (byCondition && service && !walkIn) void questionsFirst(service);
    else setStep('time');
  };

  /** Track CGS — one person goes on as before; a party is the party flow's (CGS-D10). */
  const choosePeople = (people: number): void => {
    setPartySize(people);
    setFailure(null);
    if (people > 1) return;
    if (byCondition && service && !walkIn) void questionsFirst(service);
    else setStep('time');
  };

  /**
   * Takes the slot, then moves on (to the details, or back to the review). From the time step a
   * failure keeps them on the grid. From the questions, a time taken meanwhile sends them back to
   * pick another, answers kept; anything else (offline) keeps them where they are.
   */
  const holdAndContinue = async (
    from: 'time' | 'questions' = 'time',
    to: 'details' | 'review' = 'details',
  ): Promise<void> => {
    if (!slotStart || !service || !location) return;
    // One hold at a time. Through a slow connection a second tap used to send a second hold for
    // the same time, which the first had just taken: "someone took that time", for their own hold.
    if (holding.current) return;
    holding.current = true;
    setBusy(true);
    setFailure(null);
    try {
      const held = await bookingApi.hold(slug, {
        serviceId: service.id,
        locationId: location.id,
        startAt: slotStart,
      });
      setHold({ token: held.holdToken, expiresAt: held.expiresAt });
      setStep(to);
    } catch (error) {
      const gone = error instanceof BookingApiError && error.isSlotGone;
      if (from === 'questions' && gone) {
        setFailure({ key: 'error.slotGoneKept' });
        setSlotStart(null);
        setStep('time');
      } else {
        setFailure(messageOf(error));
        if (from === 'time') setSlotStart(null);
      }
    } finally {
      holding.current = false;
      setBusy(false);
    }
  };

  /** Time → the questions, when the service asks some; otherwise straight to the hold. */
  const continueFromTime = async (): Promise<void> => {
    if (!slotStart || !service) return;
    setFailure(null);
    let state: BookingFormsState = forms;
    if (forms.serviceId !== service.id || forms.load !== 'ready') {
      setBusy(true);
      const pending =
        forms.serviceId === service.id && forms.load === 'loading' && formsLoading.current
          ? formsLoading.current
          : loadForms(service.id);
      const list = await pending;
      setBusy(false);
      if (list === null) {
        setFailure({ key: 'questions.loadFailed' });
        return;
      }
      state = formsReducer(
        formsReducer(NO_FORMS, { type: 'load_start', serviceId: service.id }),
        { type: 'loaded', serviceId: service.id, forms: list },
      );
    }
    const next = hasForms(state) ? firstUnsettled(state) : -1;
    if (next < 0) {
      await holdAndContinue('time');
      return;
    }
    dispatch({ type: 'open', index: next, returnTo: null });
    setStep('questions');
  };

  const holdLive = (): boolean => hold !== null && Date.parse(hold.expiresAt) > Date.now();

  /**
   * The questions are done: on to the details, holding the time now (SFM-D26). Settles once the
   * hold has, so the form's Continue says "One moment…" until then.
   */
  const leaveQuestions = (state: BookingFormsState): Promise<void> => {
    const to = state.returnTo === 'review' ? 'review' : 'details';
    dispatch({ type: 'returned' });
    // BCW-D5 — by condition the time comes next, and nothing is held yet.
    if (byCondition && to === 'details' && !holdLive()) {
      setStep('time');
      return Promise.resolve();
    }
    if (holdLive()) {
      setStep(to);
      return Promise.resolve();
    }
    return holdAndContinue('questions', to);
  };

  /** After a form is answered or skipped: the next one still to answer, or on. */
  const afterForm = (state: BookingFormsState): Promise<void> => {
    const next = state.returnTo === 'review' ? -1 : firstUnsettled(state);
    if (next >= 0) {
      dispatch({ type: 'open', index: next });
      return Promise.resolve();
    }
    return leaveQuestions(state);
  };

  const finishForm = (checked: Answers): Promise<void> => {
    const after = formsReducer(forms, { type: 'finish', checked });
    dispatch({ type: 'finish', checked });
    return afterForm(after);
  };

  const skipForm = (): void => {
    const after = formsReducer(forms, { type: 'skip' });
    dispatch({ type: 'skip' });
    void afterForm(after);
  };

  /** Back from a form's first screen: its intro, the form before, or the times. */
  const backFromForm = (): void => {
    setFailure(null);
    const entry = forms.entries[forms.cursor];
    if (forms.view.kind === 'runner' && entry && !entry.form.required) {
      dispatch({ type: 'open', index: forms.cursor, view: { kind: 'intro' } });
      return;
    }
    if (forms.cursor > 0) {
      dispatch({ type: 'open', index: forms.cursor - 1 });
      return;
    }
    // The times: any hold (the Change path) is given back.
    void releaseHold();
    dispatch({ type: 'returned' });
    // BCW-D5 — by condition the questions come first: back to the branch, or the service.
    if (byCondition) {
      if (eligibleLocations.length > 1) setStep('location');
      else if (conditionServices.length > 1) {
        setServiceId('');
        setStep('service');
      } else leaveCondition();
      return;
    }
    setStep('time');
  };

  /** "Choose another service", from "Unable to continue": everything of this one goes. */
  const chooseAnotherService = (): void => {
    void releaseHold();
    dispatch({ type: 'reset' });
    setConditionId('');
    setServiceId('');
    setSlotStart(null);
    setFailure(null);
    setStep('service');
  };

  /** Change on a form, from the review: that form, keeping the hold, then back to the review. */
  const changeForm = (index: number): void => {
    setFailure(null);
    dispatch({ type: 'open', index, returnTo: 'review' });
    setStep('questions');
  };

  /**
   * Track SFM (SFM-D35) — answers in memory clear after 20 idle minutes, as a form link's
   * session does, with "Keep going" two minutes before (WCAG 2.2.1).
   */
  const answering = forms.entries.some(
    (entry) => entry.status !== 'not_started' || Object.keys(entry.answers).length > 0,
  );
  useEffect(() => {
    if (!answering || booked) {
      setIdleUntil(null);
      return;
    }
    setIdleUntil((current) => current ?? new Date(Date.now() + IDLE_MS).toISOString());
  }, [answering, booked]);
  const stillHere = (): void => {
    if (!answering) return;
    const now = Date.now();
    if (now - lastActivity.current < 30_000) return;
    lastActivity.current = now;
    setIdleUntil(new Date(now + IDLE_MS).toISOString());
  };
  const clearForPrivacy = (): void => {
    void releaseHold();
    dispatch({ type: 'reset' });
    setConditionId('');
    setServiceId('');
    setSlotStart(null);
    setPatient(emptyPatient());
    setIntake({});
    setConsent(false);
    setTexts(false);
    setIdleUntil(null);
    setStep('service');
    setFailure({ key: 'questions.timedOut' });
  };
  const privacy = usePrivacyTimeout(
    idleUntil ? { idleUntil, until: null } : null,
    clearForPrivacy,
  );

  /** Details → Review, once the form is complete. Nothing is sent yet. */
  const review = (): void => {
    if (!service) return;
    const patientErrors = validatePatient(patient);
    const intakeErrors = validateIntake(formSchema, intake);
    const consentError: FieldErrors =
      service.consentText && !consent
        ? { consent: 'error.consentRequired' }
        : {};
    const all = { ...patientErrors, ...intakeErrors, ...consentError };
    setErrors(all);
    if (hasErrors(all)) {
      // Announce by moving to the first bad field, rather than scrolling
      // silently to a red box a screen reader never mentions. The summary above
      // the form links to every one of them.
      const first = Object.keys(all)[0]!;
      document.getElementById(fieldDomId(first))?.focus();
      return;
    }
    setFailure(null);
    setStep('review');
  };

  const submit = async (): Promise<void> => {
    if (!hold || !service) return;
    setBusy(true);
    setFailure(null);
    try {
      const answers = confirmForms(forms, locale);
      const result = await bookingApi.confirm(slug, {
        holdToken: hold.token,
        patient: patient as PublicPatientDetails,
        intakeAnswers: formSchema ? intake : undefined,
        consentGiven: consent,
        consentTextVersion: service.consentText ? hashOf(service.consentText) : undefined,
        invitationToken,
        idempotencyKey: attemptKey.current,
        // Both or neither: the language says which wording a yes was given in.
        ...(textOffer ? { textReminders: textsChosen, language: locale } : {}),
        ...(answers ? { forms: answers } : {}),
        // BCW-D5 — the condition the patient came in by.
        ...(condition ? { conditionId: condition.id } : {}),
      });
      setHold(null);
      setBooked(result);
    } catch (error) {
      // A fresh key: this attempt is over, and a retry is a new booking.
      attemptKey.current = cryptoId();
      if (error instanceof BookingApiError && (await formRefusal(error))) return;
      setFailure(messageOf(error));
      // A 404 at confirm is a hold that went (its row reused by another booking).
      if (error instanceof BookingApiError && (error.isSlotGone || error.isMissing)) {
        setHold(null);
        setSlotStart(null);
        setStep('time');
        if (hasForms(forms)) setFailure({ key: 'error.slotGoneKept' });
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Track SFM (SFM-D31) — the server refused the booking's forms, and booked nothing: back to
   * the form it names, keeping the hold. True when handled.
   */
  const formRefusal = async (error: BookingApiError): Promise<boolean> => {
    if (!service) return false;
    const formId = typeof error.extra.formId === 'string' ? error.extra.formId : null;
    const index = formId ? forms.entries.findIndex((entry) => entry.form.formId === formId) : -1;
    switch (error.code) {
      case 'form_answers_required': {
        // The service's forms changed since they loaded: load them again, then the first missing.
        const list = await loadForms(service.id);
        const missing = Array.isArray(error.extra.formIds) ? (error.extra.formIds as string[]) : [];
        const at = (list ?? []).findIndex((form) => missing.includes(form.formId));
        if (at >= 0) {
          dispatch({ type: 'open', index: at, returnTo: 'review' });
          setStep('questions');
        }
        setFailure({ key: 'review.formsChanged' });
        return true;
      }
      case 'invalid_answers': {
        const errors = (error.extra.errors ?? {}) as Record<string, AnswerError>;
        if (index < 0 || Object.keys(errors).every((key) => key === '_form')) {
          await loadForms(service.id);
          setFailure({ key: 'review.formsChanged' });
          return true;
        }
        dispatch({
          type: 'open',
          index,
          view: { kind: 'runner', mount: 0, startAtReview: true, errors },
          returnTo: 'review',
        });
        setStep('questions');
        return true;
      }
      case 'form_stopped': {
        if (index < 0) return false;
        const questionId =
          typeof error.extra.questionId === 'string' ? error.extra.questionId : undefined;
        dispatch({
          type: 'open',
          index,
          view: { kind: 'runner', mount: 0, startAt: questionId },
          returnTo: 'review',
        });
        setStep('questions');
        return true;
      }
      case 'forms_unavailable':
        // Confirm again books without them; the pharmacy sends them later.
        dispatch({ type: 'unavailable' });
        setFailure({ key: 'review.formsUnavailable' });
        return true;
      default:
        return false;
    }
  };

  if (joining && service) {
    return (
      <WaitlistJoin
        page={page}
        service={service}
        condition={condition}
        locations={eligibleLocations}
        waitlistOnly={service.bookingMode === 'waitlist'}
        runsOn={clinicWeekdays}
        onBack={() => {
          setJoining(false);
          setFailure(null);
          // A waitlist service has no times to go back to.
          if (service.bookingMode === 'waitlist') {
            setServiceId('');
            setStep('service');
          }
        }}
      />
    );
  }

  if (booked) {
    return (
      <Confirmation
        booking={booked}
        locale={locale}
        context={{ slug, serviceId, locationId }}
      />
    );
  }

  // Track CGS (CGS-D10 to D13) — a party of two or more is booked by its own flow.
  if (partySize > 1 && service && location && offersParty) {
    const upTo = steps.indexOf('people');
    return (
      <PartyFlow
        page={page}
        service={service}
        location={location}
        size={partySize}
        before={steps.slice(0, upTo + 1).map((entry) => t(STEP_LABEL[entry]))}
        conditionId={condition?.id}
        forms={forms.serviceId === service.id && forms.load === 'ready' ? forms.entries.map((entry) => entry.form) : null}
        formsReady={() =>
          forms.serviceId === service.id && forms.load === 'loading' && formsLoading.current
            ? formsLoading.current
            : loadForms(service.id)
        }
        initialDate={date || firstDayAt(location.timezone)}
        window={clinicDates}
        onBack={() => {
          setPartySize(1);
          setStep('people');
        }}
      />
    );
  }

  const formEntry = step === 'questions' ? forms.entries[forms.cursor] : undefined;
  const formHeading = formEntry
    ? textIn(formEntry.form.schema.title, formLang(formEntry.form, locale))
    : null;

  return (
    <div className="space-y-6" onKeyDown={stillHere} onPointerDown={stillHere}>
      <Steps steps={steps} current={stepIndex} />

      <h1 ref={headingRef} tabIndex={-1} className={HEADING}>
        {step === 'time'
          ? walkIn
            ? t('walkin.heading')
            : service
              ? t('time.headingFor', { service: service.title })
              : t('time.heading')
          : step === 'questions'
            ? formHeading
            : step === 'service' && condition && conditionServices.length > 1
              ? t('condition.chooseService', { condition: conditionText(condition, locale).name })
              : t(STEP_HEADING[step])}
        {step === 'time' && service && <EnglishOnly />}
        {formEntry && formLang(formEntry.form, locale) !== locale && <EnglishOnly />}
      </h1>

      {failure && <Alert>{'key' in failure ? t(failure.key) : failure.text}</Alert>}
      {step === 'time' && forms.load === 'failed' && service && (
        <Button variant="outline" onClick={() => void loadForms(service.id)}>
          {t('questions.retry')}
        </Button>
      )}
      {privacy.warning && idleUntil && (
        <TimeoutDialog
          busy={false}
          onKeepGoing={() => setIdleUntil(new Date(Date.now() + IDLE_MS).toISOString())}
          onClose={clearForPrivacy}
        />
      )}

      {step === 'service' &&
        (condition && conditionServices.length > 1 ? (
          <ConditionServicesStep
            services={conditionServices}
            onChoose={(next) => chooseService(next, true)}
            onAll={leaveCondition}
          />
        ) : (
          <ServiceStep
            services={page.services}
            onChoose={chooseListedService}
            conditions={conditions}
            onChooseCondition={chooseCondition}
          />
        ))}
      {/* Track CGS (CGS-D2) — the clinics listed now, each on its own page. */}
      {step === 'service' && !clinic && clinicHref && page.clinics && page.clinics.length > 0 && (
        <ClinicsList clinics={page.clinics} hrefFor={clinicHref} />
      )}

      {step === 'location' && (
        <LocationStep locations={eligibleLocations} onChoose={chooseLocation} />
      )}

      {step === 'people' && service && partyOf(service) && (
        <PeopleStep
          service={service}
          value={partySize}
          onContinue={choosePeople}
          onBack={goBack}
          showBack={stepIndex > 0}
        />
      )}

      {step === 'time' && service && walkIn && (
        <WalkInStep locations={eligibleLocations} phone={page.profile.supportPhone} onBack={goBack} />
      )}

      {step === 'time' && service && location && !walkIn && (
        <TimeStep
          slug={slug}
          service={service}
          location={location}
          date={date}
          onDate={setDate}
          selected={slotStart}
          onSelect={setSlotStart}
          onContinue={() => void continueFromTime()}
          onBack={goBack}
          busy={busy}
          phone={location.phone ?? page.profile.supportPhone}
          note={service.bookingMode === 'book_or_walk_in' ? <WalkInNote location={location} /> : null}
          onWaitlist={offersWaitlist ? () => setJoining(true) : undefined}
          dates={clinicDates}
        />
      )}

      {step === 'questions' && location && formEntry && (
        <QuestionsStep
          state={forms}
          pageLang={locale}
          today={zonedToday(location.timezone)}
          phone={location.phone ?? page.profile.supportPhone ?? null}
          notice={hold ? <HoldNotice hold={hold} /> : null}
          busy={busy}
          onAnswers={(answers: DraftAnswers) => dispatch({ type: 'answers', answers })}
          onFinish={finishForm}
          onSkip={skipForm}
          onAnswerNow={() =>
            dispatch({
              type: 'open',
              index: forms.cursor,
              view: { kind: 'runner', mount: 0 },
            })
          }
          onBack={backFromForm}
          onLeave={chooseAnotherService}
        />
      )}

      {step === 'details' && service && (
        <DetailsStep
          service={service}
          formSchema={formSchema}
          collectsHealthCard={page.collectsHealthCard}
          patient={patient}
          onPatient={setPatient}
          intake={intake}
          onIntake={(id, value) =>
            setIntake((current) => ({ ...current, [id]: value }))
          }
          consent={consent}
          onConsent={setConsent}
          textOffer={textOffer}
          texts={texts}
          onTexts={setTexts}
          errors={errors}
          hold={hold}
          onSubmit={review}
          onBack={goBack}
        />
      )}

      {step === 'review' && service && location && slotStart && (
        <ReviewStep
          service={service}
          location={location}
          startAt={slotStart}
          patient={patient}
          consentText={service.consentText}
          texts={textOffer ? textsChosen : null}
          hold={hold}
          forms={
            hasForms(forms) ? (
              <FormsSummary
                entries={forms.entries}
                pageLang={locale}
                pharmacy={page.profile.displayName}
                onChange={changeForm}
              />
            ) : null
          }
          onConfirm={() => void submit()}
          onChange={() => {
            setFailure(null);
            setStep('details');
          }}
          busy={busy}
        />
      )}
    </div>
  );
}

/**
 * "(en anglais)" beside a service title on the French page. Titles are stored
 * in one language today (B9-Q1); saying so beats silently mixing languages.
 */
function EnglishOnly() {
  const { t, locale } = useI18n();
  if (locale === 'en') return null;
  return <span className="ml-2 text-base font-normal text-muted-foreground">{t('service.englishOnly')}</span>;
}

// ── Steps ────────────────────────────────────────────────────────────────────

/**
 * Where the patient is: numbered circles joined by a line, done ones ticked. On a phone only the
 * current step's name shows beside its circle; the others are still read out, so the bar says
 * every step to a screen reader and to the tests alike.
 */
function Steps({ steps, current }: { steps: Step[]; current: number }) {
  const { t } = useI18n();
  return <StepBar labels={steps.map((step) => t(STEP_LABEL[step]))} current={current} />;
}

function ServiceStep({
  services,
  onChoose,
  conditions,
  onChooseCondition,
}: {
  services: PublicService[];
  onChoose: (service: PublicService) => void;
  conditions: PublicCondition[];
  onChooseCondition: (condition: PublicCondition) => void;
}) {
  return (
    <ServicePicker
      services={services}
      onChoose={onChoose}
      titleSuffix={<EnglishOnly />}
      conditions={conditions}
      onChooseCondition={onChooseCondition}
    />
  );
}

/** Track BCW (BCW-D2) — the two or three services a condition leads to. */
function ConditionServicesStep({
  services,
  onChoose,
  onAll,
}: {
  services: PublicService[];
  onChoose: (service: PublicService) => void;
  onAll: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-5">
      <ul className="space-y-3">
        {services.map((entry) => (
          <li key={entry.id}>
            <ChoiceCard
              icon={categoryIcon(entry.category)}
              onClick={() => onChoose(entry)}
              title={
                <>
                  {entry.title}
                  <EnglishOnly />
                </>
              }
              meta={
                <Chip icon={Clock}>{t('service.duration', { minutes: entry.durationMin })}</Chip>
              }
            />
          </li>
        ))}
      </ul>
      <Button variant="outline" onClick={onAll}>
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        {t('condition.allServices')}
      </Button>
    </div>
  );
}

function LocationStep({
  locations,
  onChoose,
}: {
  locations: PublicLocation[];
  onChoose: (location: PublicLocation) => void;
}) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {locations.map((location) => (
        <li key={location.id}>
          <ChoiceCard
            icon={MapPin}
            onClick={() => onChoose(location)}
            title={location.name}
            detail={location.address}
          />
        </li>
      ))}
    </ul>
  );
}

/** How far ahead "Next available" looks, and what the empty state promises. */
const LOOKAHEAD_DAYS = 14;

/** Marks `count` days from `start` as having open times or none, from one look's slots. */
function markDays(
  into: Map<string, boolean>,
  start: string,
  count: number,
  slots: AvailabilitySlot[],
  timeZone: string,
): Map<string, boolean> {
  for (let i = 0; i < count; i += 1) into.set(addDays(start, i), false);
  for (const slot of slots) into.set(zonedDateOf(new Date(slot.startUtc), timeZone), true);
  return into;
}

export function TimeStep({
  slug,
  service,
  location,
  date,
  onDate,
  selected,
  onSelect,
  onContinue,
  onBack,
  busy,
  phone,
  backLabel,
  continueLabel,
  note,
  onWaitlist,
  partySize,
  dates,
}: {
  slug: string;
  service: PublicService;
  location: PublicLocation;
  date: string;
  onDate: (date: string) => void;
  selected: string | null;
  onSelect: (startUtc: string) => void;
  onContinue: () => void;
  onBack: () => void;
  busy: boolean;
  /** Who to call when nothing is open online. */
  phone: string | null;
  /** The manage page reuses this step with its own words for the two buttons. */
  backLabel?: string;
  continueLabel?: string;
  /** Track BCW — "Or walk in today: …" for a book-or-walk-in service. */
  note?: ReactNode;
  /** Track BCW — "None of these work? Join the waitlist". The manage page never passes it. */
  onWaitlist?: () => void;
  /** Track CGS — times where a whole party of this many fits (CGS-D10); one person by default. */
  partySize?: number;
  /** Track CGS — a clinic's first and last dates: where to look, and how far. */
  dates?: { from: string; to: string };
}) {
  const { t, locale } = useI18n();
  const [slots, setSlots] = useState<AvailabilitySlot[] | null>(null);
  const [loading, setLoading] = useState(false);
  /** A failed load is an error with Retry, never "No times" (A5 FE-P-04). */
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  /** The first open time in the next two weeks; null = none, undefined = still looking. */
  const [next, setNext] = useState<AvailabilitySlot | null | undefined>(undefined);
  /** Days known to have open times (true) or none (false): the week strip's dots. */
  const [known, setKnown] = useState<ReadonlyMap<string, boolean>>(() => new Map());
  const today = zonedToday(location.timezone);
  /** Where "Next available" looks from: today, or a clinic's first date ahead. */
  const lookFrom = dates && dates.from > today ? dates.from : today;
  const people = partySize && partySize > 1 ? { partySize } : {};
  /** The first of the seven days the strip shows. */
  const [pageStart, setPageStart] = useState(() => pageStartFor(date || today, today));
  /** Later weeks already looked at, so paging back and forth asks once. */
  const looked = useRef(new Set<string>());

  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    const dayWindow = zonedDayWindow(date, location.timezone);
    void bookingApi
      .availability(slug, {
        serviceId: service.id,
        locationId: location.id,
        ...dayWindow,
        ...people,
      })
      .then((result) => !cancelled && setSlots(result))
      .catch(() => {
        if (cancelled) return;
        setSlots(null);
        setFailed(true);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // `people` comes from partySize, which is here.
  }, [slug, service.id, location.id, location.timezone, date, attempt, partySize]);

  // One look across the next two weeks, so an empty day still points somewhere — and the strip
  // knows which of those days have times.
  useEffect(() => {
    let cancelled = false;
    setNext(undefined);
    looked.current = new Set();
    const first = lookFrom;
    const from = zonedDayWindow(first, location.timezone).from;
    const to = zonedDayWindow(addDays(first, LOOKAHEAD_DAYS), location.timezone).from;
    void bookingApi
      .availability(slug, { serviceId: service.id, locationId: location.id, from, to, ...people })
      .then((result) => {
        if (cancelled) return;
        setNext(result[0] ?? null);
        setKnown(markDays(new Map(), first, LOOKAHEAD_DAYS, result, location.timezone));
      })
      // Not knowing the next time is not worth an error of its own; the day's grid has one.
      .catch(() => !cancelled && setNext(undefined));
    return () => {
      cancelled = true;
    };
  }, [slug, service.id, location.id, location.timezone, attempt, lookFrom, partySize]);

  // A week past the look-ahead: one look at its seven days, for its dots. A failure only means
  // no dots; the day's own grid still says what is open.
  useEffect(() => {
    // On a clinic's page a week before its first date has no look-ahead of its own.
    const ahead = daysBetween(lookFrom, pageStart);
    if (ahead < LOOKAHEAD_DAYS && (!dates || ahead >= 0)) return;
    if (looked.current.has(pageStart)) return;
    looked.current.add(pageStart);
    let cancelled = false;
    const from = zonedDayWindow(pageStart, location.timezone).from;
    const to = zonedDayWindow(addDays(pageStart, 7), location.timezone).from;
    void bookingApi
      .availability(slug, { serviceId: service.id, locationId: location.id, from, to, ...people })
      .then((result) => {
        if (!cancelled) setKnown((current) => markDays(new Map(current), pageStart, 7, result, location.timezone));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [slug, service.id, location.id, location.timezone, pageStart, partySize]);

  // A day chosen another way (typed, or "Next available") brings the strip to its week.
  useEffect(() => {
    if (date) setPageStart(pageStartFor(date, zonedToday(location.timezone)));
  }, [date, location.timezone]);

  // How far ahead this service may be booked, so the picker cannot offer a date
  // the server will refuse.
  const maxDate = useMemo(() => {
    // Track CGS (CGS-D4) — a clinic's last date replaces the service's window.
    if (dates) return dates.to;
    const limit = new Date();
    limit.setUTCDate(limit.getUTCDate() + service.bookingWindowDays);
    return limit.toISOString().slice(0, 10);
  }, [service.bookingWindowDays, dates]);

  const nextLabel = useMemo(
    () =>
      next
        ? new Intl.DateTimeFormat(intlLocale(locale), {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZone: location.timezone,
          }).format(new Date(next.startUtc))
        : null,
    [next, locale, location.timezone],
  );

  const count = slots?.length ?? 0;
  const nothingSoon = next === null;
  const emptyDay = !loading && count === 0;

  return (
    <div className="space-y-6">
      {note}
      {next && nextLabel && (
        <button
          type="button"
          onClick={() => {
            onDate(zonedDateOf(new Date(next.startUtc), location.timezone));
            onSelect(next.startUtc);
          }}
          className={cn(
            'group flex w-full items-center gap-3 rounded-xl border border-primary/25 bg-accent px-4 py-3 text-left transition-colors hover:border-primary/60',
            FOCUS_RING,
          )}
        >
          <IconTile icon={Zap} size="sm" className="bg-card" />
          <span className="min-w-0 flex-1 text-base font-semibold text-primary-strong">
            {t('time.nextAvailable', { when: nextLabel })}
          </span>
          <ChevronRight className="h-5 w-5 shrink-0 text-primary-strong" aria-hidden="true" />
        </button>
      )}

      {nothingSoon && (
        <Card tone="muted" className="text-base">
          {phone ? t('time.noneSoonCall', { phone }) : t('time.noneSoon')}
        </Card>
      )}

      <WeekStrip
        today={today}
        start={pageStart}
        selected={date}
        last={maxDate}
        known={known}
        onSelect={onDate}
        onPage={setPageStart}
      />

      {/*
        A typeable date field, not a calendar-only widget. `input[type=date]`
        is keyboard-operable and screen-reader-labelled by the platform, and it
        gives a native picker on a phone for free. The strip above is the quick way.
      */}
      <div className="grid gap-3 sm:grid-cols-[minmax(0,15rem)_1fr] sm:items-end sm:gap-5">
        <Field id="booking-date" label={t('time.date')} help={t('time.dateHelp')}>
          {(props) => (
            <Input
              {...props}
              type="date"
              value={date}
              min={today}
              max={maxDate}
              onChange={(event) => onDate(event.target.value)}
            />
          )}
        </Field>
        <p className="flex items-center gap-2 text-sm text-muted-foreground sm:pb-3.5">
          <Globe className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t('time.timezoneNote', { location: location.name })}
        </p>
      </div>

      {failed ? (
        <Alert
          action={
            <Button variant="outline" size="sm" onClick={() => setAttempt(attempt + 1)}>
              {t('error.retry')}
            </Button>
          }
        >
          {t('time.loadFailed')}
        </Alert>
      ) : (
        <div className="space-y-4 border-t border-border pt-5">
          <div
            className={cn(
              emptyDay && 'flex flex-col items-center gap-3 rounded-xl border border-dashed border-border px-4 py-8 text-center',
            )}
          >
            {emptyDay && <IconTile icon={CalendarX2} tone="muted" size="lg" />}
            {/*
              The count is announced. Replacing the grid is a big visual change and a
              completely silent one without this. One element throughout, so it stays a
              live region while the day changes.
            */}
            <p
              aria-live="polite"
              className={cn('text-base font-semibold text-heading', emptyDay && 'max-w-sm font-medium text-muted-foreground')}
            >
              {loading
                ? t('time.loading')
                : count === 0
                  ? t('time.none')
                  : count === 1
                    ? t('time.availableOne')
                    : t('time.available', { count })}
            </p>
          </div>

          {loading ? (
            <TimesSkeleton />
          ) : (
            <SlotGrid
              slots={slots ?? []}
              timeZone={location.timezone}
              selected={selected}
              onSelect={onSelect}
              label={t('time.gridLabel')}
            />
          )}
        </div>
      )}

      {onWaitlist && (
        <button
          type="button"
          onClick={onWaitlist}
          className={cn(
            'group flex w-full items-center gap-3 rounded-xl border border-dashed border-primary/40 bg-accent/40 px-4 py-3.5 text-left transition-colors hover:border-primary hover:bg-accent',
            FOCUS_RING,
          )}
        >
          <IconTile icon={BellRing} size="sm" className="bg-card" />
          <span className="min-w-0 flex-1 text-base font-semibold text-primary-strong">{t('waitlist.cta')}</span>
          <ChevronRight className="h-5 w-5 shrink-0 text-primary-strong" aria-hidden="true" />
        </button>
      )}

      <StepActions>
        <Button variant="outline" onClick={onBack}>
          {!backLabel && <ChevronLeft className="h-4 w-4" aria-hidden="true" />}
          {backLabel ?? t('step.back')}
        </Button>
        <Button onClick={onContinue} disabled={!selected} loading={busy} className="flex-1 sm:flex-none">
          {continueLabel ?? t('step.continue')}
        </Button>
      </StepActions>
    </div>
  );
}

/** Where the times will be, while they load: the shape of the grid, said by the count above. */
function TimesSkeleton() {
  return (
    <div aria-hidden="true" className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
      {Array.from({ length: 10 }, (_, index) => (
        <span key={index} className="h-12 rounded-lg bg-muted motion-safe:animate-pulse" />
      ))}
    </div>
  );
}

function DetailsStep({
  service,
  formSchema,
  collectsHealthCard,
  patient,
  onPatient,
  intake,
  onIntake,
  consent,
  onConsent,
  textOffer,
  texts,
  onTexts,
  errors,
  hold,
  onSubmit,
  onBack,
}: {
  service: PublicService;
  formSchema: ReturnType<typeof safeFormSchema>;
  collectsHealthCard: boolean;
  patient: Partial<PublicPatientDetails>;
  onPatient: (next: Partial<PublicPatientDetails>) => void;
  intake: Record<string, unknown>;
  onIntake: (id: string, value: unknown) => void;
  consent: boolean;
  onConsent: (value: boolean) => void;
  /** `null` where the pharmacy does not text reminders: no box. */
  textOffer: { wordings: PublicTextReminderWording[] } | null;
  texts: boolean;
  onTexts: (value: boolean) => void;
  errors: FieldErrors;
  hold: { token: string; expiresAt: string } | null;
  onSubmit: () => void;
  onBack: () => void;
}) {
  const { t } = useI18n();
  const errorKeys = Object.keys(errors).filter((key) => errors[key]);

  const set = (key: keyof PublicPatientDetails, value: string): void =>
    onPatient({ ...patient, [key]: value });

  const text = (
    key: keyof PublicPatientDetails,
    label: MessageKey,
    options: { type?: string; help?: MessageKey; required?: boolean; autoComplete?: string } = {},
  ) => (
    <Field
      id={`patient-${key}`}
      label={t(label)}
      help={options.help ? t(options.help) : undefined}
      error={errors[key] ? t(errors[key]) : undefined}
      required={options.required}
      requiredLabel={t('details.required')}
    >
      {(props) => (
        <Input
          {...props}
          type={options.type ?? 'text'}
          autoComplete={options.autoComplete}
          value={(patient[key]) ?? ''}
          onChange={(event) => set(key, event.target.value)}
        />
      )}
    </Field>
  );

  return (
    // `noValidate` so our own localised messages are shown rather than the
    // browser's, which ignore the page language entirely.
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      className="space-y-7"
    >
      <HoldNotice hold={hold} />

      {/* Every problem, each linking to its field (WCAG 3.3.1). */}
      {errorKeys.length > 0 && (
        <div role="alert" className="rounded-xl border border-danger/30 bg-danger/5 p-4 text-base">
          <p className="flex items-center gap-2 font-semibold text-heading">
            <AlertCircle className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
            {t('error.summary')}
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-12">
            {errorKeys.map((key) => (
              <li key={key}>
                <a
                  href={`#${fieldDomId(key)}`}
                  onClick={(event) => {
                    event.preventDefault();
                    document.getElementById(fieldDomId(key))?.focus();
                  }}
                  className="text-danger underline underline-offset-4"
                >
                  {fieldLabel(key, t, formSchema)}: {t(errors[key]!)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <section aria-labelledby="details-about" className="space-y-5">
        <h2 id="details-about" className={SECTION_TITLE}>
          <IconTile icon={User} size="sm" />
          {t('details.aboutYou')}
        </h2>
        <div className="grid gap-5 sm:grid-cols-2">
          {text('firstName', 'details.firstName', {
            required: true,
            autoComplete: 'given-name',
          })}
          {text('lastName', 'details.lastName', {
            required: true,
            autoComplete: 'family-name',
          })}
        </div>

        <div className="sm:w-1/2 sm:pr-2.5">
          {text('dob', 'details.dob', {
            type: 'date',
            help: 'details.dobHelp',
            required: true,
            autoComplete: 'bday',
          })}
        </div>

        {collectsHealthCard &&
          text('healthCard', 'details.healthCard', { help: 'details.healthCardHelp' })}
      </section>

      <section aria-labelledby="details-reach" className="space-y-5 border-t border-border pt-6">
        <h2 id="details-reach" className={SECTION_TITLE}>
          <IconTile icon={Mail} size="sm" />
          {t('details.reachYou')}
        </h2>
        {/* The two boxes line up even though only the email has a line of help above it. */}
        <div className="grid gap-5 sm:grid-cols-2 sm:items-end">
          {text('email', 'details.email', {
            type: 'email',
            help: 'details.emailHelp',
            required: true,
            autoComplete: 'email',
          })}
          {text('phone', 'details.phone', {
            type: 'tel',
            required: true,
            autoComplete: 'tel',
          })}
        </div>

        {textOffer && patient.phone?.trim() && (
          <TextRemindersBox wordings={textOffer.wordings} checked={texts} onChange={onTexts} />
        )}
      </section>

      {formSchema && (
        <section aria-labelledby="intake-heading" className="space-y-5 border-t border-border pt-6">
          <h2 id="intake-heading" className="text-lg font-semibold text-heading">
            {service.title}
          </h2>
          <IntakeForm
            schema={formSchema}
            answers={intake}
            errors={errors}
            onChange={onIntake}
            t={t}
          />
        </section>
      )}

      {service.consentText && (
        <section
          aria-labelledby="consent-heading"
          className="space-y-3 border-t border-border pt-6"
        >
          <h2 id="consent-heading" className="text-lg font-semibold text-heading">
            {t('consent.heading')}
          </h2>
          {/* The pharmacist's own words, shown as plain text — never as markup. */}
          <p className="whitespace-pre-wrap rounded-xl bg-muted/70 p-4 text-base text-foreground">
            {service.consentText}
          </p>
          <ConsentBox
            checked={consent}
            onChange={onConsent}
            error={errors.consent ? t(errors.consent) : undefined}
          />
        </section>
      )}

      <StepActions>
        <Button variant="outline" onClick={onBack}>
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          {t('step.back')}
        </Button>
        <Button type="submit" className="flex-1 sm:flex-none">
          {t('step.continue')}
        </Button>
      </StepActions>
    </form>
  );
}

/** A section's title with its icon: "About you", "How we can reach you". */
const SECTION_TITLE = 'flex items-center gap-2.5 text-lg font-semibold text-heading';

/**
 * "I have read and agree to the above." as a tile the whole of which ticks the box. The box
 * itself keeps the name and the error, so a screen reader hears both where it lands.
 */
export function ConsentBox({
  checked,
  onChange,
  error,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  error?: string;
}) {
  const { t } = useI18n();
  return (
    <>
      <label
        htmlFor="consent"
        className={cn(
          'flex min-h-12 cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors',
          checked ? 'border-primary bg-accent/60' : 'border-input hover:border-primary/60',
          error && !checked && 'border-danger',
        )}
      >
        <input
          id="consent"
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? 'consent-error' : undefined}
          className={CHECKBOX}
        />
        <span className="text-base font-medium text-heading">{t('consent.agree')}</span>
      </label>
      {error && (
        <p id="consent-error" className="flex items-center gap-1.5 text-sm font-medium text-danger">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </>
  );
}

/**
 * The 10-minute hold, shown only in its last minute (or once it has gone) — a
 * countdown from the first second reads as pressure on a form that rarely
 * takes that long.
 */
function HoldNotice({ hold }: { hold: { expiresAt: string } | null }) {
  const { t } = useI18n();
  const msLeft = useHoldCountdown(hold?.expiresAt ?? null);
  if (msLeft === null || msLeft > 60_000) return null;
  return (
    <p
      aria-live="polite"
      className={cn(
        'flex items-center gap-2.5 rounded-xl border px-4 py-3 text-base font-medium text-heading',
        msLeft > 0 ? 'border-warning/50 bg-warning/15' : 'border-danger/30 bg-danger/5',
      )}
    >
      <Clock
        className={cn('h-5 w-5 shrink-0', msLeft > 0 ? 'text-warning-foreground' : 'text-danger')}
        aria-hidden="true"
      />
      {msLeft > 0 ? t('details.holdTimer') : t('details.holdExpired')}
    </p>
  );
}

/** Everything the patient is about to confirm, in one place (FE-B9-04). */
function ReviewStep({
  service,
  location,
  startAt,
  patient,
  consentText,
  texts,
  hold,
  forms,
  onConfirm,
  onChange,
  busy,
}: {
  service: PublicService;
  location: PublicLocation;
  startAt: string;
  patient: Partial<PublicPatientDetails>;
  consentText: string | null;
  /** Track RBA — whether they chose text reminders, or `null` where none were offered. */
  texts: boolean | null;
  hold: { expiresAt: string } | null;
  /** Track SFM rev 2 — "Your answers", when the service asked some. */
  forms?: ReactNode;
  onConfirm: () => void;
  onChange: () => void;
  busy: boolean;
}) {
  const { t, locale } = useI18n();
  const when = new Intl.DateTimeFormat(intlLocale(locale), {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: location.timezone,
  }).format(new Date(startAt));
  const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ');
  const contact = [patient.phone, patient.email].filter(Boolean).join(' · ');

  return (
    <div className="space-y-6">
      <HoldNotice hold={hold} />
      <div className="overflow-hidden rounded-xl border border-border">
        <AppointmentHead
          iso={startAt}
          timeZone={location.timezone}
          first={{
            label: t('done.what'),
            value: (
              <>
                {service.title}
                <EnglishOnly />
              </>
            ),
          }}
          second={{
            label: t('done.when'),
            value: `${when} (${t('review.minutes', { minutes: service.durationMin })})`,
          }}
        />
        <dl className="divide-y divide-border px-4 sm:px-5">
          <Detail
            icon={MapPin}
            label={t('done.where')}
            value={[location.name, location.address].filter(Boolean).join(' · ')}
          />
          <Detail icon={User} label={t('review.who')} value={name} />
          {contact && <Detail icon={Mail} label={t('review.contact')} value={contact} />}
          {texts !== null && (
            <Detail
              icon={MessageSquare}
              label={t('review.texts')}
              value={texts ? t('review.textsYes', { phone: patient.phone ?? '' }) : t('review.textsNo')}
            />
          )}
        </dl>
      </div>

      {forms}

      {consentText && (
        <section aria-labelledby="review-consent" className="space-y-2">
          <h2 id="review-consent" className="text-lg font-semibold text-heading">
            {t('review.consent')}
          </h2>
          {/* The pharmacist's own words, as plain text. */}
          <p className="whitespace-pre-wrap rounded-xl bg-muted/70 p-4 text-base text-foreground">
            {consentText}
          </p>
        </section>
      )}

      <StepActions>
        <Button variant="outline" onClick={onChange} disabled={busy}>
          {t('review.change')}
        </Button>
        <Button onClick={onConfirm} loading={busy} className="flex-1 sm:flex-none">
          {busy ? t('review.submitting') : t('review.confirm')}
        </Button>
      </StepActions>
    </div>
  );
}

// ── Confirmation ─────────────────────────────────────────────────────────────

/**
 * Where the manage page can find this booking's pharmacy, service and branch.
 *
 * The manage response carries none of them (backend follow-up), and Change time
 * needs all three to look up open times and hold one — so the link from this
 * page carries them. None is personal: they are the pharmacy's own public ids.
 */
export function manageHref(
  token: string,
  context?: { slug: string; serviceId: string; locationId: string },
): string {
  const base = `/manage/${encodeURIComponent(token)}`;
  if (!context?.slug || !context.serviceId || !context.locationId) return base;
  const query = new URLSearchParams({
    p: context.slug,
    s: context.serviceId,
    l: context.locationId,
  });
  return `${base}?${query.toString()}`;
}

export function Confirmation({
  booking,
  locale,
  context,
}: {
  booking: PublicBookingResult;
  locale: string;
  context?: { slug: string; serviceId: string; locationId: string };
}) {
  const { t } = useI18n();
  const when = useMemo(
    () =>
      new Intl.DateTimeFormat(intlLocale(locale), {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: booking.location.timezone,
      }).format(new Date(booking.startAt)),
    [booking, locale],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center gap-4 pt-2 text-center">
        <SuccessMark />
        {/* `tabIndex={-1}` + autofocus: the whole page changed, so say so. */}
        <h1 tabIndex={-1} ref={(node) => node?.focus()} className={HEADING}>
          {t('done.heading')}
        </h1>
        {/* Only what is true (Q8): no promise of an email until delivery is confirmed. */}
        <p className="max-w-md text-base text-muted-foreground">
          {CONFIRMATION_IS_SENT && booking.patient.email
            ? t('done.emailed', { email: booking.patient.email })
            : t('done.saveThis')}
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        <AppointmentHead
          iso={booking.startAt}
          timeZone={booking.location.timezone}
          first={{ label: t('done.when'), value: when }}
          second={{ label: t('done.what'), value: booking.serviceTitle }}
        />
        <dl className="px-4 sm:px-5">
          <Detail
            icon={MapPin}
            label={t('done.where')}
            value={[booking.location.name, booking.location.address]
              .filter(Boolean)
              .join(' · ')}
          />
        </dl>
      </div>

      {/* Track SFM: the forms this visit asks for, with a session for this device. */}
      <FormsDue forms={booking.forms ?? []} />

      <Link to={manageHref(booking.manageToken, context)} className={buttonClasses('primary', 'md')}>
        {t('done.manageLink')}
      </Link>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** The label an error summary names a field by. */
function fieldLabel(
  key: string,
  t: (key: MessageKey) => string,
  schema: ReturnType<typeof safeFormSchema>,
): string {
  const patientLabels: Record<string, MessageKey> = {
    firstName: 'details.firstName',
    lastName: 'details.lastName',
    dob: 'details.dob',
    email: 'details.email',
    phone: 'details.phone',
    healthCard: 'details.healthCard',
    consent: 'consent.heading',
  };
  const known = patientLabels[key];
  if (known) return t(known);
  return schema?.fields.find((field) => field.id === key)?.label ?? key;
}

/** The DOM id a validation error belongs to, for moving focus there. */
function fieldDomId(key: string): string {
  if (key === 'consent') return 'consent';
  const patientFields = [
    'firstName',
    'lastName',
    'dob',
    'email',
    'phone',
    'healthCard',
  ];
  return patientFields.includes(key) ? `patient-${key}` : `intake-${key}`;
}

/**
 * Either one of our own translated messages, or the server's own sentence.
 *
 * A tagged pair rather than `MessageKey | string`, which TypeScript widens
 * straight back to `string` — losing every guarantee that a key exists. It also
 * replaces guessing which one a value is: an earlier version sniffed for a dot
 * and no spaces, which would have silently swallowed a server message like
 * "Not.Available" and rendered nothing at all.
 */
type Failure = { key: MessageKey } | { text: string };

function messageOf(error: unknown): Failure {
  if (!(error instanceof BookingApiError)) return { key: 'error.unknown' };
  if (error.message === 'offline') return { key: 'error.offline' };
  if (error.isSlotGone) return { key: 'error.slotGone' };
  if (error.message === 'unknown') return { key: 'error.unknown' };
  // The server's own wording, which is usually better than anything generic.
  return { text: error.message };
}

/** Random enough to make an idempotency key unique per attempt. */
function cryptoId(): string {
  return `pub-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}
